'use client';
import React, { useState } from 'react';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { BellIcon, Bars3Icon, ChevronRightIcon, ArrowRightOnRectangleIcon, UserCircleIcon, Cog6ToothIcon,  } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import UnifiedSearch from '@/components/layout/UnifiedSearch';
import { useNotifications } from '@/hooks/useNotifications';
import NotificationPanel from '@/components/layout/NotificationPanel';
import { usePreferences } from '@/contexts/PreferencesContext';

interface HeaderProps {
  pageTitle?: string;
  breadcrumb?: string[];
  role?: string;
  onMobileMenuToggle: () => void;
}

const roleLabels: Record<string, { name: string; role: string; avatar: string }> = {
  student: { name: 'Mary Teine', role: 'Student — Waigani Campus', avatar: 'MT' },
  staff: { name: 'James Koro', role: 'Trainer — Development Admin', avatar: 'JK' },
  coordinator: { name: 'Susan Naime', role: 'Program Coordinator', avatar: 'SN' },
  executive: { name: 'Michael Barobe', role: 'Chief Executive Officer', avatar: 'MB' },
  sponsor: { name: 'PNG Department of Finance', role: 'Sponsor Organisation', avatar: 'DF' },
  reporting: { name: 'Admin User', role: 'ICT / System Admin', avatar: 'AU' },
};

const SEVERITY_COLORS: Record<string, string> = {
  info: 'bg-blue-500',
  warning: 'bg-amber-500',
  error: 'bg-red-500',
  success: 'bg-emerald-500',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

export default function Header({ pageTitle, breadcrumb, role, onMobileMenuToggle }: HeaderProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const { user, signOut } = useAuth();
  const { prefs } = usePreferences();
  const router = useRouter();
  const { notifications, unreadCount, loading, markAsRead, markAllRead, clearAll } = useNotifications(prefs.notification_frequency);

  // Derive display info from authenticated user or fallback to role defaults
  const defaultUser = roleLabels[role] || roleLabels['student'];
  const displayName = user?.user_metadata?.full_name || defaultUser.name;
  const displayRole = defaultUser.role;
  const avatarInitials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.replace('/login');
    } catch {
      router.replace('/login');
    }
  };

  const handleNotifClick = async (id: string, isRead: boolean) => {
    if (!isRead) {
      await markAsRead(id);
    }
  };

  return (
    <header
      role="banner"
      className="bg-card border-b border-border h-16 flex items-center px-4 lg:px-6 gap-4 flex-shrink-0 card-shadow z-10"
    >
      {/* Mobile menu */}
      <button
        onClick={onMobileMenuToggle}
        className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
        aria-label="Open navigation menu"
        aria-expanded={false}
        aria-controls="portal-sidebar"
      >
        <Bars3Icon className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
      </button>

      {/* Breadcrumb */}
      <div className="hidden md:flex flex-shrink-0 min-w-0 max-w-[200px]">
        {breadcrumb && breadcrumb.length > 0 ? (
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center gap-1.5 text-sm text-muted-foreground list-none p-0 m-0">
              {breadcrumb.map((crumb, i) => (
                <li key={`crumb-${crumb}`} className="flex items-center gap-1.5">
                  {i > 0 && <ChevronRightIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                  <span
                    className={i === breadcrumb.length - 1 ? 'text-foreground font-600' : ''}
                    aria-current={i === breadcrumb.length - 1 ? 'page' : undefined}
                  >
                    {crumb}
                  </span>
                </li>
              ))}
            </ol>
          </nav>
        ) : (
          <h1 className="text-lg font-700 text-foreground truncate">{pageTitle}</h1>
        )}
      </div>

      {/* Mobile page title (shown when breadcrumb hidden) */}
      <div className="flex md:hidden flex-1 min-w-0">
        <h1 className="text-base font-700 text-foreground truncate">{pageTitle}</h1>
      </div>

      {/* Unified Search */}
      <div className="hidden md:flex flex-1 justify-center px-2">
        <UnifiedSearch role={role} />
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2" role="toolbar" aria-label="Header actions">
        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => { setShowNotifs(!showNotifs); setShowDropdown(false); }}
            className="relative p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label={unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications'}
            aria-expanded={showNotifs}
            aria-haspopup="true"
          >
            <BellIcon className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 rounded-full flex items-center justify-center"
                aria-hidden="true"
              >
                <span className="text-white text-[9px] font-700 px-0.5">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              </span>
            )}
          </button>
          <NotificationPanel
            open={showNotifs}
            onClose={() => setShowNotifs(false)}
            notifications={notifications}
            unreadCount={unreadCount}
            loading={loading}
            markAsRead={markAsRead}
            markAllRead={markAllRead}
            clearAll={clearAll}
          />
        </div>

        {/* User dropdown */}
        <div className="relative">
          <button
            onClick={() => { setShowDropdown(!showDropdown); setShowNotifs(false); }}
            className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
            aria-label={`User menu for ${displayName}`}
            aria-expanded={showDropdown}
            aria-haspopup="true"
          >
            <div
              className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-700"
              aria-hidden="true"
            >
              {avatarInitials}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-600 text-foreground leading-tight">{displayName}</p>
              <p className="text-xs text-muted-foreground leading-tight truncate max-w-[160px]">{displayRole}</p>
            </div>
          </button>
          {showDropdown && (
            <div
              className="absolute right-0 top-full mt-2 w-52 bg-card border border-border rounded-xl card-shadow-md z-50 animate-fade-in"
              role="menu"
              aria-label="User account menu"
            >
              <div className="px-4 py-3 border-b border-border">
                <p className="font-600 text-sm text-foreground">{displayName}</p>
                <p className="text-xs text-muted-foreground">{displayRole}</p>
              </div>
              <div className="py-1">
                <Link
                  href="/account-settings"
                  onClick={() => setShowDropdown(false)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  role="menuitem"
                >
                  <UserCircleIcon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  My Profile
                </Link>
                <Link
                  href="/account-settings"
                  onClick={() => setShowDropdown(false)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
                  role="menuitem"
                >
                  <Cog6ToothIcon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  Settings
                </Link>
                <div className="border-t border-border my-1" role="separator" />
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-negative hover:bg-negative-bg transition-colors"
                  role="menuitem"
                >
                  <ArrowRightOnRectangleIcon className="w-4 h-4" aria-hidden="true" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}